import { cn } from '@crowdex/ui/lib/utils';
import { ChevronDown, Maximize2, MessageSquare } from 'lucide-react';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { CORE_LIVE_VIEW } from '@/features/plugins/core-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { activePanelView } from '@/lib/side-panel/panel-tabs';
import {
  CallControls,
  CallConversations,
  CallError,
  CallRequest,
  CallStartIcon,
  CallStatus,
  CallSteps,
  CallTranscript,
  CallView,
  iconButton,
} from './call-parts';
import { LiveClock } from './live-chip';
import { liveChip, speakingAssistant, useLivePlace } from './live-place';
import { useOpenLive } from './open-live';
import { type LiveCall, useLiveCall } from './use-live-call';

const LiveCallContext = createContext<{ call: LiveCall; enabled: boolean } | null>(null);

/** The user's live call, which lives above the conversations. */
export function useLiveCallContext() {
  return useContext(LiveCallContext);
}

/** The Live tab is on screen: the call shows there, not in its floating panel. */
function useLiveTabShown(): boolean {
  return useDesktopShellStore(
    (state) => state.sidePanelOpen && activePanelView(state.panelTabs) === CORE_LIVE_VIEW,
  );
}

/**
 * Hosts the live call above the conversations, so that switching between them keeps it. It shows
 * in the Live tab (the nav rail's Live); elsewhere, while it runs, in a panel floating over the
 * app.
 */
export function LiveCallProvider({
  enabled,
  openConversation,
  openWorkspace,
  children,
}: {
  enabled: boolean;
  /** The conversation on the user's screen: the assistant's context. */
  openConversation: string | null;
  /** The folder on screen, where the assistant starts when the open conversation is new. */
  openWorkspace: string | null;
  children: ReactNode;
}) {
  const place = useLivePlace(enabled ? openConversation : null);
  const call = useLiveCall({
    openConversation: enabled ? openConversation : null,
    openWorkspace: enabled ? openWorkspace : null,
    placeAssistant: place.assistant,
  });
  const tabShown = useLiveTabShown();
  return (
    <LiveCallContext.Provider value={{ call, enabled }}>
      {children}
      {enabled && !tabShown && (call.status !== 'idle' || call.error !== null) && (
        <LiveCallPanel call={call} />
      )}
    </LiveCallContext.Provider>
  );
}

/** The call button of a composer: calls the assistant, the open conversation as its context. */
export function LiveCallButton({
  disabled,
  variant = 'icon',
}: {
  disabled: boolean;
  /** `primary`: the composer's filled button while nothing is typed (D5, mobile 3). */
  variant?: 'icon' | 'primary';
}) {
  const context = useLiveCallContext();
  if (context === null || !context.enabled || context.call.status !== 'idle') return null;
  return (
    <button
      aria-label="Call the assistant"
      className={
        variant === 'primary'
          ? 'flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
          : iconButton
      }
      disabled={disabled}
      onClick={() => void context.call.start()}
      title="Talk with your assistant in a voice call"
      type="button"
    >
      <CallStartIcon aria-hidden="true" className="size-4" />
    </button>
  );
}

/** Whether the Live button can show: Live is on and no call is going. */
export function useLiveCallAvailable(): boolean {
  const context = useLiveCallContext();
  return context?.enabled === true && context.call.status === 'idle';
}

/**
 * The call in picture in picture (D61), while the Live tab is not on screen: a thumbnail at the
 * bottom right, under the Summary card pinned at the top: its line (time, who the user talks to),
 * the assistant's face, its latest words and the controls; ⤢ goes back to the call. What was said
 * and done unfolds under it, by itself when something waits on the user.
 */
export function LiveCallPanel({ call }: { call: LiveCall }) {
  const [unfolded, setUnfolded] = useState(false);
  const opener = useConversationOpener();
  const openLive = useOpenLive();
  const inCall = call.status !== 'idle';
  const open = !inCall || unfolded || call.request !== null || call.error !== null;
  const said = call.transcript.at(-1);
  // Beside the conversation, the panel's bottom is free (the Summary card is at its top);
  // without the panel, the thumbnail sits above the composer and its send button.
  const inPanel = useDesktopShellStore((state) => state.sidePanelOpen && state.panelSize < 100);
  const chip = liveChip(speakingAssistant(call));
  return (
    <section
      aria-label="Call with the assistant"
      className={cn(
        'fixed right-4 z-50 flex w-72 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-hairline bg-popover text-popover-foreground shadow-lg max-sm:top-3 max-sm:bottom-auto',
        inPanel ? 'bottom-4' : 'bottom-36',
      )}
      data-placement={inPanel ? 'panel' : 'above-composer'}
      data-testid="live-pip"
    >
      {inCall && (
        <>
          <div className="type-caption flex items-center gap-2 px-3 pt-2.5">
            <LiveClock call={call} suffix={call.group?.title ?? chip.name} />
            <span className="min-w-0 flex-1" />
            <button
              aria-label="Back to the call"
              className={iconButton}
              onClick={() => void openLive()}
              title="Back to the call"
              type="button"
            >
              <Maximize2 className="size-3.5" />
            </button>
          </div>
          <div className="relative flex h-28 items-center justify-center max-sm:hidden">
            <span aria-hidden className="absolute size-24 rounded-full bg-foreground/[0.05]" />
            <CallView
              call={call}
              face
              className={cn(
                'pointer-events-none size-20 rounded-full bg-muted ring-offset-2 ring-offset-popover transition-shadow',
                call.status === 'speaking' ? 'ring-2 ring-foreground' : 'ring-1 ring-hairline',
              )}
            />
          </div>
          <div className="flex flex-col gap-2.5 px-3 pb-3">
            <div className="flex items-center gap-2 sm:hidden">
              <CallStatus call={call} />
            </div>
            {said !== undefined && (
              <p
                className={cn(
                  'line-clamp-2 text-xs leading-relaxed max-sm:hidden',
                  said.role === 'user' && 'text-muted-foreground',
                )}
              >
                {said.role === 'user' ? said.text : `« ${said.text} »`}
              </p>
            )}
            <div className="flex items-center justify-center gap-2">
              <span className="max-sm:hidden">
                <CallStatus call={call} />
              </span>
              <span className="min-w-0 flex-1 max-sm:hidden" />
              <CallControls call={call} size="sm" />
              <button
                aria-expanded={open}
                aria-label={open ? 'Hide call details' : 'Show call details'}
                className={iconButton}
                onClick={() => setUnfolded(!open)}
                type="button"
              >
                <ChevronDown
                  aria-hidden="true"
                  className={cn('size-3.5 transition-transform', open && 'rotate-180')}
                />
              </button>
            </div>
          </div>
        </>
      )}
      {open && (
        <div
          className={cn(
            'flex flex-col gap-2.5 border-hairline px-3 pb-3 text-xs',
            inCall ? 'border-t pt-2.5' : 'pt-3',
          )}
        >
          <CallError call={call} />
          {inCall && (
            <>
              <CallTranscript call={call} className="max-h-36 overflow-y-auto" />
              <CallSteps
                call={call}
                className="max-h-28 overflow-y-auto border-hairline border-t pt-2.5"
              />
              <CallConversations call={call} />
              <CallRequest call={call} className="text-xs" />
              {call.assistant !== null && opener !== null && (
                <button
                  className="flex h-control-sm items-center justify-center gap-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    if (call.assistant !== null) void opener.openConversation(call.assistant);
                  }}
                  title="See everything the assistant does, live"
                  type="button"
                >
                  <MessageSquare aria-hidden="true" className="size-3.5" />
                  Open conversation
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
